import React, { useState } from 'react';
import { FaPhone, FaClock, FaEllipsisV } from 'react-icons/fa';
import RescheduleModal from './RescheduleModal';

const AppointmentCard = ({ appointment, onViewDetails, onRescheduleSuccess }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isRescheduleModalOpen, setIsRescheduleModalOpen] = useState(false);

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const isoString = dateString;
    const datePart = isoString.split('T')[0];
    const timePart = isoString.split('T')[1];
    const [, month, day] = datePart.split('-');
    const [hours24, minutes] = timePart.split(':');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthName = monthNames[parseInt(month) - 1];
    const dayNum = parseInt(day);
    const hours = parseInt(hours24);
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    const suffix = dayNum % 10 === 1 && dayNum !== 11 ? 'st' : dayNum % 10 === 2 && dayNum !== 12 ? 'nd' : dayNum % 10 === 3 && dayNum !== 13 ? 'rd' : 'th';
    return `${dayNum}${suffix} ${monthName}, ${displayHours}:${minutes}${ampm}`;
  };

  const getTestBadges = () => {
    const badges = [];
    if (appointment.appointmentTests && Array.isArray(appointment.appointmentTests)) {
      appointment.appointmentTests.forEach((test, idx) => {
        const testCode = test.availableTest?.test_code || '';
        const testFullName = test.availableTest?.name || '';
        let testName = '';
        let color = '';

        if (testCode.startsWith('P')) {
          testName = 'BLOOD PRO';
          color = 'bg-red-100 text-red-700';
        } else if (testCode === '003554') {
          testName = 'DEXA';
          color = 'bg-purple-100 text-purple-700';
        } else if (testCode === '003555' || testCode === '003556') {
          testName = 'VO2 MAX';
          color = 'bg-red-100 text-red-700';
        } else if (testFullName.toUpperCase().includes('BASIC')) {
          testName = 'BLOOD BASIC';
          color = 'bg-blue-100 text-blue-700';
        }

        if (testName && !badges.some(b => b.name === testName)) {
          badges.push({ name: testName, color });
        }
      });
    }
    return badges;
  };

  const branchName = appointment.facility?.branch_name 
    ? appointment.facility.branch_name.replace('VITAL INSIGHTS-', '')
    : 'Unknown';

  const testBadges = getTestBadges();
  const rawPhoneNumber = appointment.user?.phone || 'N/A';
  
  // Format phone number - remove country code (+91) and format as 5 5 digits
  const formatPhoneNumber = (phone) => {
    if (phone === 'N/A') return 'N/A';
    // Remove all non-digit characters
    const digits = phone.replace(/\D/g, '');
    // Remove country code if it starts with 91 (India)
    const cleanDigits = digits.startsWith('91') && digits.length === 12 
      ? digits.slice(2) 
      : digits.slice(-10);
    // Format as XXXXX XXXXX (5 digits, space, 5 digits)
    return cleanDigits.length === 10 
      ? `${cleanDigits.slice(0, 5)} ${cleanDigits.slice(5)}`
      : phone;
  };
  
  const phoneNumber = formatPhoneNumber(rawPhoneNumber);

  const handleCall = (e) => {
    e.stopPropagation();
    if (rawPhoneNumber !== 'N/A') {
      window.location.href = `tel:${rawPhoneNumber}`;
    }
  };

  const handleRescheduleClick = (e) => {
    e.stopPropagation();
    setIsRescheduleModalOpen(true);
    setIsMenuOpen(false);
  };

  const handleRescheduleSuccess = () => {
    setIsRescheduleModalOpen(false);
    if (onRescheduleSuccess) {
      onRescheduleSuccess();
    }
  };

  return (
    <>
      <div className="bg-white border-l-4 border-orange-500 rounded p-3 hover:shadow-md transition-shadow cursor-pointer relative" onClick={() => onViewDetails(appointment)}>
        {/* Dropdown Menu Button */}
        <div className="absolute top-2 right-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsMenuOpen(!isMenuOpen);
            }}
            className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
          >
            <FaEllipsisV size={14} />
          </button>

          {/* Dropdown Menu */}
          {isMenuOpen && (
            <div className="absolute right-0 mt-1 w-40 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
              <button
                onClick={handleRescheduleClick}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-orange-50 hover:text-orange-700 transition-colors first:rounded-t-lg last:rounded-b-lg"
              >
                Reschedule
              </button>
            </div>
          )}
        </div>

        {/* Header Row: Name, Age/Gender, Reports */}
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <h3 className="font-semibold text-gray-900 text-xs">
            {appointment.profile?.name || 'Unknown'}
          </h3>
          <span className="text-xs text-gray-600 bg-gray-100 px-2 py-0.5 rounded">
            {appointment.profile?.age || '-'}/{appointment.profile?.gender?.charAt(0)?.toUpperCase() || '-'}
          </span>
          {testBadges.length > 0 && (
            <div className="flex gap-1">
              {testBadges.map((badge) => (
                <span key={badge.name} className={`text-xs px-2 py-0.5 rounded font-medium ${badge.color}`}>
                  {badge.name}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* 2x2 Grid: IDs and Details */}
        <div className="grid grid-cols-2 gap-3 mb-3 text-xs text-gray-600">
          <div>
            <span>Sukra ID: <span className="font-medium text-gray-900">{appointment.sukra_id || 'N/A'}</span></span>
          </div>
          <div>
            <span>S ID: <span className="font-medium text-gray-900">{appointment.sukra_order_id || 'N/A'}</span></span>
          </div>
          <div>
            <span>Branch: <span className="font-medium text-gray-900">{branchName}</span></span>
          </div>
          <div className="flex items-center gap-1">
            <FaClock size={11} className="text-gray-500" />
            <span>Time: <span className="font-medium text-gray-900">{formatDateTime(appointment.slot_start_time)}</span></span>
          </div>
        </div>

        {/* Call CTA */}
        <button
          onClick={handleCall}
          className="w-full py-2 px-3 bg-red-100 hover:bg-red-200 text-red-700 rounded text-xs font-semibold transition-colors flex items-center justify-center gap-2"
        >
          <FaPhone size={12} />
          {phoneNumber}
        </button>
      </div>

      {/* Reschedule Modal */}
      <RescheduleModal
        isOpen={isRescheduleModalOpen}
        onClose={() => setIsRescheduleModalOpen(false)}
        appointment={appointment}
        onSuccess={handleRescheduleSuccess}
      />
    </>
  );
};

export default AppointmentCard;
